import { useId, useState, type FC, type FormEvent } from 'react';
import { Truck } from 'lucide-react';
import type { ShipmentDetailsDto } from '@/api';
import { Alert, Button, Checkbox, Dialog, Input } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import {
  useSendShipmentToDelivery,
  useSetShipmentDelivery,
} from './api/mutations';

/*
 * Данные доставки - трек-номер (ADR-013, п. 4): при «Передать в доставку» и
 * правкой позже. Необязателен (самовывоз, своя машина). Фактическую дату
 * отгрузки менеджер не вводит - её приносит 1С реализацией (ADR-013, п. 4,
 * дополнение 2026-10-16). При передаче окно напоминает о неотмеченных
 * документах: передать они не мешают. Без предоплаты по графику передать можно
 * только авансом - флажком подтверждения (ADR-014, п. 4). Открыто ли действие,
 * решает сервер (409), а кнопку закрывает карточка.
 */

/** Длина трек-номера - как у поля отгрузки на сервере. */
const TRACKING_NUMBER_MAX_LENGTH = 100;

export interface DeliveryDialogProps {
  shipment: ShipmentDetailsDto;
  /** send - «Передать в доставку», edit - правка данных доставки. */
  mode: 'send' | 'edit';
  /** Сколько документов чек-листа не отмечено - напоминание при передаче. */
  openDocuments?: number;
  onClose: () => void;
}

export const DeliveryDialog: FC<DeliveryDialogProps> = ({
  shipment,
  mode,
  openDocuments = 0,
  onClose,
}) => {
  const formId = useId();
  const trackingFieldId = useId();
  const advanceFieldId = useId();
  const [trackingNumber, setTrackingNumber] = useState(
    shipment.trackingNumber ?? '',
  );
  const [isInAdvance, setIsInAdvance] = useState(false);
  const send = useSendShipmentToDelivery();
  const set = useSetShipmentDelivery();
  const mutation = mode === 'send' ? send : set;
  const needsAdvance = mode === 'send' && shipment.requiresAdvanceShipment;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const tracking =
      trackingNumber.trim() === '' ? null : trackingNumber.trim();
    const path = { id: shipment.id };

    if (mode === 'send')
      send.mutate(
        {
          path,
          body: { trackingNumber: tracking, shipInAdvance: isInAdvance },
        },
        { onSuccess: onClose },
      );
    else
      set.mutate(
        { path, body: { trackingNumber: tracking } },
        { onSuccess: onClose },
      );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        mode === 'send'
          ? `Передать отгрузку № ${shipment.number} в доставку`
          : `Данные доставки отгрузки № ${shipment.number}`
      }
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={mutation.isPending || (needsAdvance && !isInAdvance)}
          >
            {mode === 'send' ? (
              <>
                <Truck aria-hidden /> Передать в доставку
              </>
            ) : (
              'Сохранить'
            )}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-3.5"
      >
        {mode === 'send' && (
          <p className="m-0 text-[13px] text-ink-700">
            Отгрузка перейдёт в «Доставку». Когда 1С отгрузит заказ, она
            перейдёт в «Оплату» или «Завершено».
          </p>
        )}
        {mode === 'send' && openDocuments > 0 && (
          <Alert tone="warn">
            Не отмечено документов: {openDocuments}. Передать это не мешает.
          </Alert>
        )}
        {needsAdvance && (
          <div className="flex flex-col gap-2">
            <Alert tone="warn">
              Предоплата по графику заказа не внесена. По норме на отгрузку
              уходит только оплаченное.
            </Alert>
            <label
              htmlFor={advanceFieldId}
              className="flex cursor-pointer items-center gap-2 text-[13px] text-ink-800"
            >
              <Checkbox
                id={advanceFieldId}
                checked={isInAdvance}
                onChange={(event) => setIsInAdvance(event.target.checked)}
              />
              Отгрузить авансом - до предоплаты
            </label>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={trackingFieldId}
            className="text-xs font-medium text-ink-700"
          >
            Трек-номер
          </label>
          <Input
            id={trackingFieldId}
            value={trackingNumber}
            maxLength={TRACKING_NUMBER_MAX_LENGTH}
            placeholder="Если груз едет с перевозчиком"
            onChange={(event) => setTrackingNumber(event.target.value)}
          />
          <span className="text-[12px] text-ink-500">
            Фактическую дату отгрузки 1С подставит сама, когда бухгалтер
            проведёт реализацию.
          </span>
        </div>
        {mutation.isError && (
          <Alert tone="error">
            Не удалось сохранить: {resolveApiErrorMessage(mutation.error)}
          </Alert>
        )}
      </form>
    </Dialog>
  );
};
